import {
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { NewsResponse } from '../../libs/services/NewsService';
import { NewsListHeader } from './news-list/NewsListHeader';
import { NewsListItem } from './news-list/NewsListItem';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '350px',
        height: '100%',
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke1),
        backgroundColor: tokens.colorNeutralBackground1,
        overflow: 'auto',
    },
    list: {
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '&::-webkit-scrollbar-track': {
            background: tokens.colorNeutralBackground3,
        },
        '&::-webkit-scrollbar-thumb': {
            background: tokens.colorNeutralForeground3,
            borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: tokens.colorNeutralForeground2,
        },
    },
    newsItem: {
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...shorthands.gap(tokens.spacingVerticalXS),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    selectedNewsItem: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Selected,
        },
    },
    noNews: {
        ...shorthands.padding(tokens.spacingVerticalL),
        textAlign: 'center',
        color: tokens.colorNeutralForeground2,
    },
});

interface NewsListProps {
    onSelectNews: (id: string) => void;
    onDeleteSelected: (ids: string[]) => void;
    selectedNewsId: string;
    newsItems: NewsResponse[];
    isLoading: boolean;
}

export const NewsList: React.FC<NewsListProps> = ({
    onSelectNews,
    onDeleteSelected,
    selectedNewsId,
    newsItems,
    isLoading
}) => {
    const classes = useClasses();

    const [selectedNewsIds, setSelectedNewsIds] = useState<string[]>([]);

    const handleCheckboxChange = (newsId: string, checked: boolean) => {
        if (checked) {
            setSelectedNewsIds([...selectedNewsIds, newsId]);
        } else {
            setSelectedNewsIds(selectedNewsIds.filter(id => id !== newsId));
        }
    };

    return (
        <div className={classes.root}>
            <NewsListHeader
                selectedNewsIds={selectedNewsIds}
                onDeleteSelected={onDeleteSelected}
                setSelectedNewsIds={setSelectedNewsIds}
            />

            <div className={classes.list}>
                {isLoading ? (
                    <div className={classes.noNews}>Lade News...</div>
                ) : newsItems.length === 0 ? (
                    <div className={classes.noNews}>Keine News vorhanden</div>
                ) : (
                    newsItems.map((item) => (
                        <div
                            key={item.id}
                            className={`${classes.newsItem} ${
                                selectedNewsId === item.id ? classes.selectedNewsItem : ''
                            }`}
                        >
                            <NewsListItem
                                item={item}
                                selectedNewsIds={selectedNewsIds}
                                handleCheckboxChange={handleCheckboxChange}
                                onSelectNews={onSelectNews}
                                isSelected={selectedNewsId === item.id}
                            />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
